import { Box, Stack, Typography } from "@mui/material";
import { DisplayOnlyField } from "components/Forms/DisplayOnlyField";
import { ReactNode } from "react";
import { SectionCard } from "routes/Customer/Waste/Manifests/sections/SectionCard";

import { DifferenceChip } from "./DifferenceChip";
import { DifferenceRow } from "./differences";
import { EXPLAINED_FIELDS } from "./explainedFields";
import { FieldGroupHeading } from "./FieldGroupHeading";
import { ExplainedField, ExplanationResult } from "./types";

/**
 * One explained field: its label, the rules' answer, anything the line
 * carries instead chipped beside it, then every rule that fired, verbatim
 * and in engine order. A field with no rules says why it kept its default.
 */
export function ClassificationOutcomeCard({
  field,
  result,
  ruleDescriptions,
  difference,
}: {
  field: ExplainedField;
  result: ExplanationResult;
  ruleDescriptions: readonly string[];
  difference?: DifferenceRow;
}) {
  const spec = EXPLAINED_FIELDS[field];

  return (
    <SectionCard>
      <Stack gap={1}>
        <DisplayOnlyField
          label={spec.label}
          text={spec.value(result)}
          copyable={false}
          renderValue={
            difference &&
            ((value: ReactNode) => (
              <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                {value}
                <DifferenceChip row={difference} />
              </Stack>
            ))
          }
        />
        {ruleDescriptions.length > 0 ? (
          <Box>
            <FieldGroupHeading>Rules that applied</FieldGroupHeading>
            <Box component="ol" sx={{ m: 0, mt: 0.5, pl: 3 }}>
              {ruleDescriptions.map((description, index) => (
                <Typography key={`${description}-${index}`} component="li" variant="body2">
                  {description}
                </Typography>
              ))}
            </Box>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {spec.defaultSentence(result)}
          </Typography>
        )}
      </Stack>
    </SectionCard>
  );
}
